import {
  ApiInterface,
  ContextMenuEntryInterface,
  CustomLayer,
  MainViewInterface,
  MapViewInterface,
  MouseEventCoordinatesInterface,
  SiteModelType,
} from '@navvis/ivion';
import { DefectEventService } from './defect-event.service';
import { TranslationService } from '../services/translation.service';
import { IGlobalLocation } from './defect.models';

/** Material icon shown next to the "Report defect" entry (same as the legacy app). */
const REPORT_DEFECT_ICON = 'report';

/**
 * IVION custom layer that adds the "Report Defect" context menu entry to the
 * 2D map view. The defect location is the clicked map position on the floor
 * currently shown.
 * Migrated from web-app-legacy/js/app/Components/Defects/DefectStandardMapLayer.ts
 */
export class DefectContextMenuLayer extends CustomLayer {
  constructor(
    mapView: MapViewInterface,
    private ivApi: ApiInterface,
    private defectEventService: DefectEventService,
    private translationService: TranslationService
  ) {
    super(mapView);
  }

  /**
   * Called by IVION SDK when user right-clicks on the map.
   * Returns context menu entry for reporting defects.
   */
  public onContextMenu(pos: MouseEventCoordinatesInterface): ContextMenuEntryInterface {
    // Get 2D coordinate from screen coordinates
    const positionPromise = this.get2DCoordinate(pos);

    return {
      name: this.translationService.translate('report_defect_menu_title'),
      icon: REPORT_DEFECT_ICON,
      callback: () => {
        positionPromise.then((location) => {
          console.log('[DefectContextMenuLayer] Report defect at location:', location);

          // Emit event to Angular
          this.defectEventService.emitReportDefect({
            globalLocation: location,
          });
        }).catch((err) => {
          console.error('[DefectContextMenuLayer] Error getting position:', err);
          // Fallback to screen coordinates if we can't get world coordinates
          this.defectEventService.emitReportDefect({
            globalLocation: { x: 0, y: 0, z: 0 },
          });
        });
      },
    };
  }

  /**
   * Converts screen coordinates to world coordinates.
   * Based on legacy StandardMapLayer.get2DCoordinate implementation.
   */
  private async get2DCoordinate(pos: MouseEventCoordinatesInterface): Promise<IGlobalLocation> {
    // Unproject screen coordinates to local 2D coordinates
    const localCoordinate = this.ivApi.view.mapView.unprojectScreenCoordinates(pos.x, pos.y) as any;

    // Import three.js for Vector2
    const three = await import('three');

    // Find floor at the pointed location
    const pointedFloors = await this.ivApi.siteModel.repository.findWithin(
      new three.Vector2(localCoordinate.x, localCoordinate.y),
      SiteModelType.FLOOR
    );

    const { currentFloors } = this.ivApi.view.floorChangerService as any;

    if (!pointedFloors.length) {
      // No floors found, use current floor's z
      localCoordinate.z = currentFloors[0]?.zMin ?? 0;
      return localCoordinate;
    }

    // Find the floor that intersects with current visible floors
    const intersectingFloor = pointedFloors.find((floor: any) =>
      currentFloors.some((cf: any) => cf === floor)
    );

    localCoordinate.z = intersectingFloor?.zMin ?? currentFloors[0]?.zMin ?? 0;

    return localCoordinate;
  }
}

/**
 * IVION custom layer that adds the "Report Defect" context menu entry to the
 * panorama (main) view. The defect location is the 3D cursor position, i.e.
 * the scene point under the mouse; when IVION has no cursor data the position
 * of the current panorama image is used instead.
 * Migrated from web-app-legacy/js/app/Components/Defects/Defect3dMapLayer.ts
 */
export class DefectPanoramaContextMenuLayer extends CustomLayer {
  constructor(
    private mainView: MainViewInterface,
    private defectEventService: DefectEventService,
    private translationService: TranslationService
  ) {
    super(mainView);
  }

  public onContextMenu(): ContextMenuEntryInterface | undefined {
    const location = this.getCursorLocation() ?? this.getImageLocation();
    if (!location) {
      return undefined;
    }

    return {
      name: this.translationService.translate('report_defect_menu_title'),
      icon: REPORT_DEFECT_ICON,
      callback: () => {
        console.log('[DefectPanoramaContextMenuLayer] Report defect at location:', location);
        this.defectEventService.emitReportDefect({ globalLocation: location });
      },
    };
  }

  /** Cursor position in site coordinates, or null when IVION has no cursor data. */
  private getCursorLocation(): IGlobalLocation | null {
    try {
      const loc = this.mainView.getCurrentCursorPosition()?.location;
      return isFiniteLocation(loc) ? { x: loc.x, y: loc.y, z: loc.z } : null;
    } catch (err) {
      console.warn('[DefectPanoramaContextMenuLayer] Cursor position not available:', err);
      return null;
    }
  }

  /** Position of the panorama image currently shown, or null when none is loaded. */
  private getImageLocation(): IGlobalLocation | null {
    try {
      const loc = this.mainView.getImage()?.location;
      return isFiniteLocation(loc) ? { x: loc.x, y: loc.y, z: loc.z } : null;
    } catch (err) {
      console.warn('[DefectPanoramaContextMenuLayer] Image position not available:', err);
      return null;
    }
  }
}

function isFiniteLocation(loc: { x: number; y: number; z: number } | undefined | null): loc is { x: number; y: number; z: number } {
  return !!loc && Number.isFinite(loc.x) && Number.isFinite(loc.y) && Number.isFinite(loc.z);
}
